import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  AcknowledgeBody,
  AlertsResponse,
  AssignableUsersResponse,
  AssignBody,
  EmailType,
  InboxName,
  ItemListResponse,
  RefreshResponse,
  ResolveBody,
  SnoozeBody,
  StatsResponse,
  TriageItem,
  TriageStatus,
} from './types'

const TRIAGE_KEY = ['maildesk-triage'] as const

export interface TriageListFilters {
  inbox?: InboxName
  email_type?: EmailType
  status?: TriageStatus
  assigned_to?: number
  include_suppressed?: boolean
  include_resolved?: boolean
  /** ISO-8601 lower bound on latest_msg_at (inclusive). */
  date_from?: string
  /** ISO-8601 upper bound on latest_msg_at (inclusive). */
  date_to?: string
  limit?: number
}

function filterParams(filters: TriageListFilters): string {
  const p = new URLSearchParams()
  if (filters.inbox) p.set('inbox', filters.inbox)
  if (filters.email_type) p.set('email_type', filters.email_type)
  if (filters.status) p.set('status', filters.status)
  if (filters.assigned_to != null) p.set('assigned_to', String(filters.assigned_to))
  if (filters.include_suppressed) p.set('include_suppressed', 'true')
  if (filters.include_resolved) p.set('include_resolved', 'true')
  if (filters.date_from) p.set('date_from', filters.date_from)
  if (filters.date_to) p.set('date_to', filters.date_to)
  if (filters.limit != null) p.set('limit', String(filters.limit))
  return p.toString()
}

/** List triage items + header summary. Refetches every 30s by default. */
export function useTriageList(filters: TriageListFilters = {}) {
  const qs = filterParams(filters)
  return useQuery({
    queryKey: [...TRIAGE_KEY, 'list', filters],
    queryFn: () => jsonFetch<ItemListResponse>(`/api/maildesk-triage/items${qs ? '?' + qs : ''}`),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

export function useTriageStats() {
  return useQuery({
    queryKey: [...TRIAGE_KEY, 'stats'],
    queryFn: () => jsonFetch<StatsResponse>('/api/maildesk-triage/stats'),
    staleTime: 15_000,
    refetchInterval: 30_000,
  })
}

/** P0 banner feed. Polled every 30s when banner is mounted (D-5). */
export function useActiveAlerts(enabled = true) {
  return useQuery({
    queryKey: [...TRIAGE_KEY, 'alerts'],
    queryFn: () => jsonFetch<AlertsResponse>('/api/maildesk-triage/alerts/active'),
    enabled,
    refetchInterval: 30_000,
  })
}

function invalidateTriage(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: TRIAGE_KEY })
}

export function useResolveItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ResolveBody }) =>
      jsonFetch<TriageItem>(`/api/maildesk-triage/items/${id}/resolve`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateTriage(qc),
  })
}

export function useSnoozeItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: SnoozeBody }) =>
      jsonFetch<TriageItem>(`/api/maildesk-triage/items/${id}/snooze`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateTriage(qc),
  })
}

export function useAssignItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AssignBody }) =>
      jsonFetch<TriageItem>(`/api/maildesk-triage/items/${id}/assign`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateTriage(qc),
  })
}

export function useAcknowledgeAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AcknowledgeBody }) =>
      jsonFetch<TriageItem>(`/api/maildesk-triage/alerts/${id}/acknowledge`, {
        method: 'POST',
        body,
      }),
    onSuccess: () => invalidateTriage(qc),
  })
}

export function useAssignableUsers(enabled = true) {
  return useQuery({
    queryKey: [...TRIAGE_KEY, 'assignable-users'],
    queryFn: () =>
      jsonFetch<AssignableUsersResponse>('/api/maildesk-triage/users'),
    enabled,
    staleTime: 5 * 60_000, // 5 min
  })
}


export function useTriggerRefresh() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshResponse>('/api/maildesk-triage/refresh', {
        method: 'POST',
      }),
    onSuccess: () => invalidateTriage(qc),
  })
}

// ─── Sprint 35 chunk 9 — assistant retry + audit ─────────────────────

export interface RunAssistantResponse {
  triage_action_id: string
  agent_kind: 'orders' | 'support'
  queued: boolean
  celery_task_id: string | null
  note: string | null
}

/** Operator-triggered re-run of the inbox-assistant on one row. */
export function useRunAssistant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<RunAssistantResponse>(
        `/api/maildesk-triage/items/${id}/run-assistant`,
        { method: 'POST' },
      ),
    onSuccess: () => invalidateTriage(qc),
  })
}

export interface AuditEntry {
  id: number
  tool_name: string | null
  tier: string | null
  bundle: string | null
  status: string
  latency_ms: number | null
  result_summary: string | null
  created_at: string
}

export interface AuditResponse {
  triage_action_id: string
  entries: AuditEntry[]
  total: number
}

/** Fetch agent_audit_log rows for the row's inbox-assistant runs. */
export function useAssistantAudit(id: string, enabled = true) {
  return useQuery({
    queryKey: ['maildesk-triage', 'audit', id],
    queryFn: () =>
      jsonFetch<AuditResponse>(`/api/maildesk-triage/items/${id}/audit`),
    enabled: enabled && Boolean(id),
    staleTime: 10_000,
  })
}
